import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { gmDisplayName, gmInitial } from '../utils/displayName';

const OwnerDetail = () => {
  const { id } = useParams();
  const [owner, setOwner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchOwnerProfile();
  }, [id]);

  const fetchOwnerProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/users/public/owner/${id}`);
      const data = await response.json();
      
      if (response.ok) {
        setOwner(data);
      } else {
        setError(data.error || 'Failed to load owner profile');
      }
    } catch (err) {
      setError('Failed to load owner profile');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getPlatformIcon = (platform) => {
    if (platform === 'Xbox') {
      return '🎮'; // Xbox icon
    } else if (platform === 'PlayStation') {
      return '🎯'; // PlayStation icon
    }
    return '🎮';
  };

  const getPlatformColor = (platform) => {
    if (platform === 'Xbox') {
      return 'bg-green-100 dark:bg-green-900/30 text-green-800 border-green-300';
    } else if (platform === 'PlayStation') {
      return 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border-blue-300';
    }
    return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-300 dark:border-gray-600';
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading owner profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error}
        </div>
        <Link to="/news" className="mt-4 inline-block text-blue-600 dark:text-blue-400 hover:underline">
          ← Back to Owner News
        </Link>
      </div>
    );
  }

  if (!owner) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Back Button */}
      <Link to="/news" className="text-blue-600 dark:text-blue-400 hover:underline mb-6 inline-block">
        ← Back to Owner News
      </Link>

      {/* Owner Profile Header */}
      <div className="bg-gradient-to-br from-blue-500 to-blue-700 text-white rounded-lg shadow-xl p-8 mb-6">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          {/* Avatar */}
          <div className="w-32 h-32 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-400 font-bold text-5xl shadow-lg dark:shadow-gray-900">
            {gmInitial(owner, { preferFirstName: true })}
          </div>

          {/* Owner Info */}
          <div className="flex-1 text-center md:text-left">
            <h1 className="text-4xl font-bold mb-2">
              {gmDisplayName(owner, { preferFirstName: true })}
            </h1>
            
            {/* Gamertag and Platform */}
            <div className="flex flex-wrap gap-3 justify-center md:justify-start mb-4">
              {owner.gamertag && (
                <div className="bg-white dark:bg-gray-800/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm text-blue-100">Gamertag</span>
                  <div className="text-white font-semibold">🎮 {owner.gamertag}</div>
                </div>
              )}
              
              {owner.platform && (
                <div className="bg-white dark:bg-gray-800/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                  <span className="text-sm text-blue-100">Platform</span>
                  <div className="text-white font-semibold flex items-center gap-2">
                    {getPlatformIcon(owner.platform)} {owner.platform}
                  </div>
                </div>
              )}

              <div className="bg-white dark:bg-gray-800/20 backdrop-blur-sm px-4 py-2 rounded-lg">
                <span className="text-sm text-blue-100">Joined</span>
                <div className="text-white font-semibold">📅 {formatDate(owner.createdAt)}</div>
              </div>
            </div>

            {/* Platform Badge */}
            {owner.platform && (
              <div className="inline-flex items-center gap-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-full shadow-md dark:shadow-gray-900">
                <span className="text-2xl">{getPlatformIcon(owner.platform)}</span>
                <span className="font-bold text-gray-800 dark:text-gray-200">{owner.platform} Player</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Current Team Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
          🏀 Current Team
        </h2>
        
        {owner.team ? (
          <Link 
            to={`/teams/${owner.team.id}`}
            className="flex items-center gap-6 p-6 bg-gradient-to-r from-blue-50 to-white rounded-lg border-2 border-blue-200 dark:border-blue-700 hover:border-blue-400 transition hover:shadow-lg dark:shadow-gray-900"
          >
            {owner.team.logoUrl && (
              <img 
                src={owner.team.logoUrl} 
                alt={`${owner.team.name} logo`}
                className="w-24 h-24 object-contain"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <div className="flex-1">
              <h3 className="text-3xl font-bold text-gray-800 dark:text-gray-200">
                {owner.team.city} {owner.team.name}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 text-lg mt-1">{owner.team.conference} Conference</p>
              <div className="mt-2 inline-block px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-semibold">
                {owner.team.abbreviation}
              </div>
            </div>
            <div className="text-blue-600 dark:text-blue-400 text-xl font-bold">
              View Roster →
            </div>
          </Link>
        ) : (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p className="text-lg">Not currently assigned to a team</p>
          </div>
        )}
      </div>

      {/* Stats & Info Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-6 rounded-lg shadow-lg dark:shadow-gray-900">
          <div className="text-sm text-green-100 mb-2">League Status</div>
          <div className="text-2xl font-bold">
            {owner.role === 'team_owner' ? '👑 Team Owner' : '⏳ Pending'}
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white p-6 rounded-lg shadow-lg dark:shadow-gray-900">
          <div className="text-sm text-purple-100 mb-2">Member Since</div>
          <div className="text-2xl font-bold">{formatDate(owner.createdAt)}</div>
        </div>

        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white p-6 rounded-lg shadow-lg dark:shadow-gray-900">
          <div className="text-sm text-orange-100 mb-2">Account Status</div>
          <div className="text-2xl font-bold">✅ Active</div>
        </div>
      </div>

      {/* Platform Info */}
      {owner.platform && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg dark:shadow-gray-900 p-6">
          <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
            {getPlatformIcon(owner.platform)} Platform Information
          </h2>
          <div className={`p-6 rounded-lg border-2 ${getPlatformColor(owner.platform)}`}>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold mb-2">{owner.platform}</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {owner.platform === 'Xbox' 
                    ? 'Competing on Xbox Series S/X' 
                    : 'Competing on PlayStation 5'}
                </p>
              </div>
              <div className="text-6xl">
                {getPlatformIcon(owner.platform)}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OwnerDetail;
